import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Tooltip, IconButton, Stack } from '@mui/material';
import { styled, lighten } from '@mui/material/styles';
import LlamaLogo from '../../../../../assets/GroupByLaw.svg';
import { CitationTooltipContent } from './Citations';
import { isRunningInOutlook } from '@/taskpane/services/office';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ThumbUp, ThumbDown, ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { StyledTooltipIcon } from '../AiChatPage';
import GradientIcon from '@/taskpane/components/gradient-icon/GradientIcon';

export interface ApiCitation {
  content: { text: string; type: string };
  location: { type: string; uri: string };
  metadata: { [key: string]: any };
  displayUrl?: string;
  text_span?: { start: number; end: number };
}

export interface Message {
  id: string;
  text: string | React.ReactNode;
  rawText?: string;
  sender: 'user' | 'assistant' | 'typing_indicator' | 'system_error' | 'divider';
  timestamp?: Date;
  citations?: ApiCitation[];
  folderId?: string;
  sousDossierId?: string;
  onSourceClick?: (citation: ApiCitation, index: number, messageId: string) => void;
  onMessageBodyClick?: (messageId: string) => void;
  isActiveMessage?: boolean;
  onRetry?: (messageId: string) => void;
  onCopy?: (rawText: string) => void;
  onLike?: (messageId: string, vote: 'good' | 'bad') => void;
  onDislike?: (messageId: string, vote: 'good' | 'bad') => void;
  vote_status?: 'good' | 'bad';
  // For divider type
  sessionDate?: string;
  messageCount?: number; // kept for backwards compatibility
  firstQuestion?: string; // kept for backwards compatibility
  isCurrentSession?: boolean;
  sessionId?: string; // kept for tracking session association
  isSessionExpanded?: boolean; // kept for backwards compatibility
  onToggleSession?: (sessionId: string) => void; // kept for backwards compatibility
  // For history messages
  isHistorical?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: (messageId: string) => void;
  // For user messages that have an associated assistant response
  nextMessageId?: string;
  isNextMessageCollapsed?: boolean;
  showDateText?: boolean;
}


const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(1.5),
  maxWidth: '100%',
  "@media (max-width: 1200px)": {
    fontSize: '10px !important',
  },
}));

const Bubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'sender' && prop !== 'isActiveMessage' && prop !== 'hasCitations', // Add hasCitations
})<{ sender: Message['sender'], isActiveMessage?: boolean, hasCitations?: boolean }>(({ theme, sender, isActiveMessage, hasCitations }) => ({
  padding: theme.spacing(0.75, 2),
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.08)',
  wordWrap: 'break-word',
  whiteSpace: 'pre-line',
  position: 'relative',
  fontFamily: 'Inter, sans-serif',
  lineHeight: '1.6',

  ...(sender === 'user' && {
    marginLeft: 'auto',
    background: 'linear-gradient(116deg, rgba(219, 231, 255, 0.70) 0%, rgba(233, 216, 255, 0.70) 100%)',
    color: '#111827',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '4px',
  }),

  ...(sender === 'assistant' && {
    marginRight: 'auto',
    background: 'white',
    color: '#374151',
    border: '1px solid #E4E7EC',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '16px',
    paddingLeft: theme.spacing(3.5),
    cursor: hasCitations ? 'pointer' : 'default',
  }),

  ...(isActiveMessage && sender === 'assistant' && {
    borderWidth: '2px',
    borderColor: '#ff6136',
  }),
}));

const AssistantLogoContainer = styled(Box)(({ theme }) => ({
  width: '25px',
  height: '25px',
  position: 'absolute',
  left: '-12px',
  top: '-10px',
  // background: 'linear-gradient(139deg, #2563EB 0%, #7C3AED 100%)',
  background: 'linear-gradient(12.59deg, rgba(255, 97, 54, 0.86), #ff6136)',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '5px',
}));

const AssistantLogoImg = styled('img')({
  width: '100%',
  height: '100%',
  filter: 'brightness(0) invert(1)',
});


const TypingIndicatorBubbleContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.125, 1.875),
  background: '#F3F4F6',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.08)',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  borderBottomLeftRadius: '4px',
  borderBottomRightRadius: '16px',
  marginRight: 'auto',
}));

const TypingDot = styled(Box)(({ theme }) => ({
  width: '6px',
  height: '6px',
  backgroundColor: '#A4ACBE',
  borderRadius: '50%',
  opacity: 0.5,
  animation: 'blink 1.4s infinite both',
  '&:nth-of-type(1)': {
    animationDelay: '0s',
  },
  '&:nth-of-type(2)': {
    animationDelay: '0.2s',
  },
  '&:nth-of-type(3)': {
    animationDelay: '0.4s',
  },
  '@keyframes blink': {
    '0%': { opacity: 0.3 },
    '20%': { opacity: 1 },
    '100%': { opacity: 0.3 },
  },
}));

const CitationMarkerLink = styled(Box)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  marginLeft: theme.spacing(0.5),
  padding: '1px 3px',
  borderRadius: '4px',
  display: 'inline-block',
  verticalAlign: 'super',
  lineHeight: '1',
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: lighten(theme.palette.primary.light, 0.85),
  },
}));


const handleOpenPdfInNewTab = (
  event: React.MouseEvent, 
  s3PresignedUrl: string,
  citation: ApiCitation,
  index: number,
  folderId: string, 
  sousDossierId: string,
  isInOutlook: boolean,
  onSourceClick: (citation: ApiCitation, index: number, messageId: string) => void,
  messageId: string
) => {
  event.preventDefault(); 
  event.stopPropagation();

  if (!s3PresignedUrl) {
    return;
  }

  try {
    const encodedS3Url = s3PresignedUrl;

    const internalViewerPath = `/?#/folder/${folderId}/${sousDossierId}/chat?url=${encodedS3Url}`;
    
    if (isInOutlook) {
      window.open(internalViewerPath, '_blank', 'noopener,noreferrer');
    } else {
      onSourceClick(citation, index, messageId);
    }

  } catch (error) {
    window.open(s3PresignedUrl, '_blank', 'noopener,noreferrer');
  }
};


function formatSegmentForHtml(segment: string): string {
  let normalizedSegment = segment.replace(/\n{2,}/g, '\n\n');

  const lines = normalizedSegment.split('\n');
  const processedLines = lines.map(line => {
    const trimmedLine = line.trim();
    if (/^###\s*.+/.test(trimmedLine)) {  // Match # My Header
      return trimmedLine.replace(/^###\s*(.+)$/, '<h5>$1</h5>');
    }
    if (/^##\s*.+/.test(trimmedLine)) {
      return trimmedLine.replace(/^##\s*(.+)$/, '<b>$1</b>');
    }
    if (/^#\s*.+/.test(trimmedLine)) {  // Match # My Header
      return trimmedLine.replace(/^#\s*(.+)$/, '<h3>$1</h3>');
    }
    return line;
  });
  return processedLines.join('\n');
}

const ActionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  justifyContent: 'flex-end',
}));

// Helper function to render segments; modified to return span and rely on parent for pre-line
const renderHtmlSegment = (segment: string, key?: string) => {
  const processedHtml = formatSegmentForHtml(segment);
  return (
    <span // CHANGED from div to span
      key={key}
      // style={{ whiteSpace: 'pre-line' }} // REMOVED: This will be handled by the parent Typography
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  );
};

// Helper to get a unique key for a citation's source
const getSourceKey = (citation: ApiCitation): string => {
  const documentUrl = citation.displayUrl || citation.location?.uri;
  const pageNumber = citation.metadata?.['page_number'];
  return `${documentUrl}#${pageNumber ?? 'undefined'}`;
};

// Modified to accept isInOutlookParam and adjust rendering
const renderAnswerWithInlineCitations = (
  answerText: string,
  messageCitations: ApiCitation[] | undefined,
  isInOutlookParam: boolean, // ADDED: isInOutlook passed as a parameter
  messageId: string, // ADDED: messageId for onSourceClick
  folderId: string | undefined, // ADDED
  sousDossierId: string | undefined, // ADDED
  onSourceClick: ((citation: ApiCitation, index: number, messageId: string) => void) | undefined, // ADDED
  voteStatus: 'good' | 'bad' | null
): React.ReactNode[] => {

  if (!messageCitations || messageCitations.length === 0 || typeof answerText !== 'string') {
    return [renderHtmlSegment(answerText, 'full-text')];
  }

  const validCitations = messageCitations
    .filter(
      c => c.text_span && typeof c.text_span.start === 'number' && typeof c.text_span.end === 'number' && c.text_span.start < c.text_span.end
    )
    .sort((a, b) => {
      if (a.text_span!.start !== b.text_span!.start) {
        return a.text_span!.start - b.text_span!.start;
      }
      return a.text_span!.end - b.text_span!.end;
    });

  if (validCitations.length === 0) {
    return [renderHtmlSegment(answerText, 'full-text-valid-citations-empty')];
  }

  const sourceToDisplayData = new Map<string, { canonicalCitation: ApiCitation, displayIndex: number }>();
  let nextDisplayIndex = 1;
  validCitations.forEach(vc => {
    const key = getSourceKey(vc);
    if (!sourceToDisplayData.has(key)) {
      sourceToDisplayData.set(key, { canonicalCitation: vc, displayIndex: nextDisplayIndex++ });
    }
  });

  const parts: React.ReactNode[] = [];
  let overallLastIndex = 0;
  validCitations.forEach((currentCitation, index) => {
    const currentStart = currentCitation.text_span!.start;
    const currentEnd = currentCitation.text_span!.end + 1; 
    // const citationNumberForDisplay = index + 1;

    if (currentStart > overallLastIndex) {
      parts.push(renderHtmlSegment(answerText.substring(overallLastIndex, currentStart), `text-before-cit-${index}`));
    }

    let renderCurrentCitedText = true;
    if (index > 0) {
      const previousCitation = validCitations[index - 1];
      if (
        previousCitation.text_span!.start === currentStart &&
        previousCitation.text_span!.end === currentEnd -1 
      ) {
        renderCurrentCitedText = false;
      }
    }

    if (renderCurrentCitedText) {
      const segmentRenderStart = Math.max(overallLastIndex, currentStart);
      const segmentRenderEnd = Math.min(currentEnd, answerText.length);

      if (segmentRenderEnd > segmentRenderStart) {
        parts.push(<br />);
        parts.push(renderHtmlSegment(answerText.substring(segmentRenderStart, segmentRenderEnd), `text-cited-segment-${index}`));
      }
    }
    
    overallLastIndex = Math.max(overallLastIndex, Math.min(currentEnd, answerText.length));
    
    const currentSourceKeyVal = getSourceKey(currentCitation);
    const displayData = sourceToDisplayData.get(currentSourceKeyVal);

    if (!displayData) {
      console.error("Missing displayData for citation:", currentCitation);
      return;
    }

    const canonicalCitationForSource = displayData.canonicalCitation;
    const citationNumberForDisplay = displayData.displayIndex;
    
    let shouldPushMarker = false;
    if (renderCurrentCitedText) {
      shouldPushMarker = true;
    } else {
      if (index > 0) { 
          const previousCitation = validCitations[index - 1];
          const previousSourceKeyVal = getSourceKey(previousCitation);

          if (currentSourceKeyVal !== previousSourceKeyVal) {
              shouldPushMarker = true; 
          }
      }
    }

    if (shouldPushMarker) {
      const docUrlForMarker = canonicalCitationForSource.displayUrl || canonicalCitationForSource.location?.uri;
      const pageNumberForMarker = canonicalCitationForSource.metadata?.['page_number'];

      parts.push(
        <Tooltip
          key={`cit-marker-${currentSourceKeyVal}-${index}`}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'transparent',
                boxShadow: 'none'
              },
            },
          }}
          title={
            <CitationTooltipContent
              citationNumber={citationNumberForDisplay}
              documentName={canonicalCitationForSource.metadata?.['input_original_filename'] || 'Document'}
              pageNumber={pageNumberForMarker}
              documentType={canonicalCitationForSource.metadata?.type_document}
              documentUrl={docUrlForMarker}
              onDocumentClick={(e, url) => handleOpenPdfInNewTab(e, url, 
                canonicalCitationForSource,
                citationNumberForDisplay - 1, 
                folderId as string,
                sousDossierId as string,
                isInOutlookParam, 
                onSourceClick!,
                messageId)}
            />
          }
          placement="top"
        >
          <CitationMarkerLink onClick={(e) => handleOpenPdfInNewTab(e, docUrlForMarker, 
                canonicalCitationForSource, 
                citationNumberForDisplay - 1, 
                folderId as string,
                sousDossierId as string,
                isInOutlookParam, 
                onSourceClick!,
                messageId)}>
            [{citationNumberForDisplay}]
          </CitationMarkerLink>
        </Tooltip>
      );
    }
  });

  if (validCitations.length > 0 && overallLastIndex < answerText.length) {
    if (!answerText.substring(overallLastIndex).startsWith('\n') && !answerText.substring(overallLastIndex).startsWith('\r\n')) {
       parts.push('\n'); 
    }
  }
  
  if (overallLastIndex < answerText.length) {
    parts.push(renderHtmlSegment(answerText.substring(overallLastIndex), `text-after-all-cit`));
  }

  return parts;
};


const ExpandChevron = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  cursor: 'pointer',
  marginRight: theme.spacing(0.5),
  color: '#6B7280',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(54, 96, 244, 0.08)',
    color: '#3660F4',
  },
}));

export const ChatMessage: React.FC<Message> = ({ 
  id, 
  text, 
  rawText,
  sender, 
  citations, 
  folderId, 
  sousDossierId, 
  onSourceClick, 
  onMessageBodyClick, 
  isActiveMessage,
  onRetry,
  onCopy,
  onLike,
  onDislike,
  vote_status: initialVoteStatus,
  // Divider props
  sessionDate,
  isCurrentSession,
  showDateText = true,
  // History props
  isHistorical,
  isCollapsed,
  onToggleCollapse,
  // For user messages with associated response
  nextMessageId,
  isNextMessageCollapsed
}) => {
  const [isInOutlook, setIsInOutlook] = useState<boolean>(false);
  const [voteStatus, setVoteStatus] = useState<'good' | 'bad' | null>(initialVoteStatus as 'good' | 'bad' | null);
  useEffect(() => {
    if (window.Office && typeof window.Office.onReady === 'function') {
      window.Office.onReady(() => {
        setIsInOutlook(isRunningInOutlook());
      });
    } else {
      setIsInOutlook(isRunningInOutlook());
    }
  }, []);

  // Handle divider type - import SessionDivider at top of file
  if (sender === 'divider') {
    const SessionDivider = require('../SessionDivider').SessionDivider;
    return (
      <Box id={id}>
        <SessionDivider
          sessionDate={sessionDate || ''}
          isCurrentSession={isCurrentSession}
          showDateText={showDateText}
        />
      </Box>
    );
  }

  // Hide collapsed historical assistant messages
  if (isHistorical && isCollapsed && sender === 'assistant') {
    return null;
  }

  if (sender === 'typing_indicator') {
    return (
      <MessageContainer key={id}>
        <TypingIndicatorBubbleContent>
          <TypingDot />
          <TypingDot />
          <TypingDot />
        </TypingIndicatorBubbleContent>
      </MessageContainer>
    );
  }

  let contentToRenderOutput: React.ReactNode;
  const originalTextForCopy = typeof text === 'string' ? text : rawText || '';
  if (typeof text === 'string') {
    if (sender === 'assistant') {
      contentToRenderOutput = renderAnswerWithInlineCitations(
        text, 
        citations, 
        isInOutlook, // Crucially pass the isInOutlook state here
        id, 
        folderId, 
        sousDossierId, 
        onSourceClick,
        voteStatus
      );
    } else { // User message
      contentToRenderOutput = renderHtmlSegment(text, 'user-text');
    }
  } else { // text is already React.ReactNode
    contentToRenderOutput = text;
  }
  
  const hasCitationsForBodyClick = sender === 'assistant' && citations && citations.length > 0;


  if (sender === 'system_error') {
    return ;
  }
  
  const handleCopy = () => {
    if (onCopy) {
      onCopy(originalTextForCopy);
    }
  };

  const handleLike = () => {
    if (onLike) {
      onLike(id, 'good');
      setVoteStatus('good');
    }
  };

  const handleDislike = () => {
    if (onDislike) {
      onDislike(id, 'bad');
      setVoteStatus('bad');
    }
  };

  // Check if this is a historical user message with an associated response
  const showExpandChevron = isHistorical && sender === 'user' && nextMessageId && onToggleCollapse;

  return (
    <MessageContainer 
        key={id} 
        sx={{ 
          flexDirection: 'column',
          alignItems: sender === 'user' ? 'flex-end' : 'flex-start' 
        }}
    >
      <Box 
        id={id}
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: sender === 'user' ? 'flex-end' : 'flex-start',
          width: '100%' 
        }}
        onClick={hasCitationsForBodyClick && onMessageBodyClick ? () => onMessageBodyClick(id) : undefined}
      >
        {showExpandChevron && (
          <ExpandChevron onClick={() => onToggleCollapse!(nextMessageId!)}>
            {isNextMessageCollapsed ? (
              <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
            ) : (
              <KeyboardArrowUpIcon sx={{ fontSize: '20px' }} />
            )}
          </ExpandChevron>
        )}
        <Bubble 
          elevation={0} 
          sender={sender} 
          isActiveMessage={isActiveMessage}
          hasCitations={hasCitationsForBodyClick}
        >
          {sender === 'assistant' && (
            <AssistantLogoContainer>
              <AssistantLogoImg src={LlamaLogo} alt="L" />
            </AssistantLogoContainer>
          )}
          <Typography 
            variant="body2"
            component="div" 
            sx={{ 
              fontFamily: 'Inter, sans-serif',
              maxWidth: '600px', 
              wordWrap: 'break-word', 
              backgroundColor: 'transparent',
              whiteSpace: 'pre-line',
              "@media (max-width: 1200px)": {
                // Any specific styles for smaller screens
              },
              '& h2': {
                fontSize: '1.5em', 
                lineHeight: '1.3', 
                marginTop: '0.5em',
                marginBottom: '0.25em',
                fontWeight: 'bold', 
              },
              '& h3': {
                fontSize: '1em', 
                lineHeight: '1.3', 
                marginTop: '0.5em',
                marginBottom: '0.25em',
                fontWeight: 'bold', 
              }
            }}
          >
            {contentToRenderOutput}
          </Typography>
        {sender === 'assistant' && onRetry && onCopy && (
          <ActionButtonContainer sx={{ marginLeft: '30px' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <StyledTooltipIcon tooltipTitle="Like">
                <IconButton
                  size="small"
                  onClick={handleLike}
                  sx={{
                    padding: '4px',
                    borderRadius: '50%',
                    color: '#8f94fb',
                    animation: voteStatus ? 'none' : 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: '0 0 0 0 rgba(143, 148, 251, 0.4)',
                      },
                      '70%': {
                        boxShadow: '0 0 0 10px rgba(143, 148, 251, 0)',
                      },
                      '100%': {
                        boxShadow: '0 0 0 0 rgba(143, 148, 251, 0)',
                      },
                    },
                  }}>
                <GradientIcon>
                  {voteStatus === 'good' ? 
                  <ThumbUp fontSize="small" />
                  :
                  <ThumbUpOutlined fontSize="small" />
                  }
                </GradientIcon>
              </IconButton>
              </StyledTooltipIcon>
              <StyledTooltipIcon tooltipTitle="Dislike">
                <IconButton size="small" onClick={handleDislike}
                sx={{
                  padding: '4px',
                  borderRadius: '50%',
                  color: '#8f94fb',
                  animation: voteStatus ? 'none' : 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(143, 148, 251, 0.4)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(143, 148, 251, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(143, 148, 251, 0)',
                    },
                  },
                }}>
                  {voteStatus === 'bad' ? 
                  <ThumbDown fontSize="small" />
                  : 
                  <ThumbDownOutlined fontSize="small" />}
                </IconButton>
              </StyledTooltipIcon>
                <StyledTooltipIcon tooltipTitle="Copy">
                  <IconButton size="small" onClick={handleCopy}>
                    <GradientIcon>
                      <ContentCopyIcon fontSize="small" />
                    </GradientIcon>
                  </IconButton>
                </StyledTooltipIcon>
            </Stack>
          </ActionButtonContainer>
        )}
      </Bubble>
    </Box>
    </MessageContainer>
  );
};